// =====================================================
// HELP SERVICE - Database Layer
// =====================================================
// Service for loading help content from Supabase
// Handles: components, FAQ, tours, analytics tracking
/**
 * .tuMetrics {"tu1": 0.86, "tu2": 0.88, "tu3": 6, "notes": "Supabase CRUD for help components, FAQ, tours, and analytics tracking", "tuVersion": "2.5"}
 * .help "Loads and updates in-app help content, FAQs, tours, and usage analytics from the database."
 */

import { supabase } from '../lib/supabase';
import type {
  HelpComponent,
  HelpAction,
  HelpFAQ,
  HelpTour,
  HelpTourProgress,
  HelpVersion,
  HelpAnalytics,
  HelpFeedback,
  HelpSearchResult,
  HelpCategory,
} from '../types/help';

// =====================================================
// Help Components
// =====================================================

/**
 * Load all help components for the current app version
 */
export async function loadHelpComponents(): Promise<HelpComponent[]> {
  const { data, error } = await supabase
    .from('help_components')
    .select('*')
    .order('category', { ascending: true })
    .order('title', { ascending: true });

  if (error) {
    console.error('Error loading help components:', error);
    return [];
  }

  return data.map(row => ({
    id: row.id,
    componentId: row.component_id,
    componentName: row.component_name,
    filePath: row.file_path,
    title: row.title,
    description: row.description,
    category: row.category as HelpCategory,
    tags: row.tags || [],
    appVersion: row.app_version,
    lastUpdated: row.last_updated,
    metadata: row.metadata,
    embedding: row.embedding,
    viewCount: row.view_count || 0,
    searchCount: row.search_count || 0,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  }));
}

/**
 * Get a specific help component by ID
 */
export async function getHelpComponent(componentId: string): Promise<HelpComponent | null> {
  const { data, error } = await supabase
    .from('help_components')
    .select('*')
    .eq('component_id', componentId)
    .single();

  if (error || !data) {
    console.error('Error loading help component:', error);
    return null;
  }

  // Increment view count
  // Suspected bug: Postgrest builders are thenables without a .catch method, so this
  // call throws at runtime (caller's error handling absorbs it). Cast preserves as-is.
  await (supabase.rpc('increment_help_view', { component_id: componentId }) as unknown as Promise<unknown>).catch(console.error);

  return {
    id: data.id,
    componentId: data.component_id,
    componentName: data.component_name,
    filePath: data.file_path,
    title: data.title,
    description: data.description,
    category: data.category as HelpCategory,
    tags: data.tags || [],
    appVersion: data.app_version,
    lastUpdated: data.last_updated,
    metadata: data.metadata,
    embedding: data.embedding,
    viewCount: data.view_count || 0,
    searchCount: data.search_count || 0,
    createdAt: data.created_at,
    updatedAt: data.updated_at,
  };
}

/**
 * Get help components by category
 */
export async function getHelpComponentsByCategory(category: HelpCategory): Promise<HelpComponent[]> {
  const { data, error } = await supabase
    .from('help_components')
    .select('*')
    .eq('category', category)
    .order('title', { ascending: true });

  if (error) {
    console.error('Error loading help components by category:', error);
    return [];
  }

  return data.map(row => ({
    id: row.id,
    componentId: row.component_id,
    componentName: row.component_name,
    filePath: row.file_path,
    title: row.title,
    description: row.description,
    category: row.category as HelpCategory,
    tags: row.tags || [],
    appVersion: row.app_version,
    lastUpdated: row.last_updated,
    metadata: row.metadata,
    embedding: row.embedding,
    viewCount: row.view_count || 0,
    searchCount: row.search_count || 0,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  }));
}

// =====================================================
// Help Actions
// =====================================================

/**
 * Get actions for a specific component
 */
export async function getComponentActions(componentId: string): Promise<HelpAction[]> {
  const { data, error } = await supabase
    .from('help_actions')
    .select('*')
    .eq('component_id', componentId)
    .order('label', { ascending: true });

  if (error) {
    console.error('Error loading component actions:', error);
    return [];
  }

  return data.map(row => ({
    id: row.id,
    componentId: row.component_id,
    actionId: row.action_id,
    label: row.label,
    description: row.description,
    ariaLabel: row.aria_label,
    dataHelpId: row.data_help_id,
    icon: row.icon,
    shortcut: row.shortcut,
    requiresRole: row.requires_role,
    steps: row.steps,
    usageCount: row.usage_count || 0,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  }));
}

// =====================================================
// FAQ
// =====================================================

/**
 * Search FAQ database (simple text search first, later add semantic)
 */
export async function searchFAQ(query: string, limit = 5): Promise<HelpFAQ[]> {
  const { data, error } = await supabase
    .from('help_faq')
    .select('*')
    .eq('is_active', true)
    .or(`question.ilike.%${query}%,answer.ilike.%${query}%`)
    .order('helpful_votes', { ascending: false })
    .limit(limit);

  if (error) {
    console.error('Error searching FAQ:', error);
    return [];
  }

  return data.map(row => ({
    id: row.id,
    question: row.question,
    answer: row.answer,
    category: row.category as HelpCategory,
    tags: row.tags || [],
    relatedComponentId: row.related_component_id,
    relatedActionIds: row.related_action_ids,
    source: row.source,
    createdBy: row.created_by,
    timesShown: row.times_shown || 0,
    helpfulVotes: row.helpful_votes || 0,
    unhelpfulVotes: row.unhelpful_votes || 0,
    avgRating: row.avg_rating,
    embedding: row.embedding,
    isActive: row.is_active,
    verified: row.verified,
    verifiedBy: row.verified_by,
    verifiedAt: row.verified_at,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  }));
}

/**
 * Get all active FAQs
 */
export async function getAllFAQs(): Promise<HelpFAQ[]> {
  const { data, error } = await supabase
    .from('help_faq')
    .select('*')
    .eq('is_active', true)
    .order('helpful_votes', { ascending: false });

  if (error) {
    console.error('Error loading FAQs:', error);
    return [];
  }

  return data.map(row => ({
    id: row.id,
    question: row.question,
    answer: row.answer,
    category: row.category as HelpCategory,
    tags: row.tags || [],
    relatedComponentId: row.related_component_id,
    relatedActionIds: row.related_action_ids,
    source: row.source,
    createdBy: row.created_by,
    timesShown: row.times_shown || 0,
    helpfulVotes: row.helpful_votes || 0,
    unhelpfulVotes: row.unhelpful_votes || 0,
    avgRating: row.avg_rating,
    embedding: row.embedding,
    isActive: row.is_active,
    verified: row.verified,
    verifiedBy: row.verified_by,
    verifiedAt: row.verified_at,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  }));
}

/**
 * Get a specific FAQ by ID
 */
export async function getFAQ(faqId: string): Promise<HelpFAQ | null> {
  const { data, error } = await supabase
    .from('help_faq')
    .select('*')
    .eq('id', faqId)
    .single();

  if (error || !data) {
    console.error('Error loading FAQ:', error);
    return null;
  }

  // Increment times_shown
  // Suspected bug: Postgrest builders are thenables without a .catch method (see note
  // above); cast preserves the existing runtime behavior.
  await (supabase
    .from('help_faq')
    .update({ times_shown: (data.times_shown || 0) + 1 })
    .eq('id', faqId) as unknown as Promise<unknown>)
    .catch(console.error);

  return {
    id: data.id,
    question: data.question,
    answer: data.answer,
    category: data.category as HelpCategory,
    tags: data.tags || [],
    relatedComponentId: data.related_component_id,
    relatedActionIds: data.related_action_ids,
    source: data.source,
    createdBy: data.created_by,
    timesShown: data.times_shown || 0,
    helpfulVotes: data.helpful_votes || 0,
    unhelpfulVotes: data.unhelpful_votes || 0,
    avgRating: data.avg_rating,
    embedding: data.embedding,
    isActive: data.is_active,
    verified: data.verified,
    verifiedBy: data.verified_by,
    verifiedAt: data.verified_at,
    createdAt: data.created_at,
    updatedAt: data.updated_at,
  };
}

/**
 * Vote on FAQ helpfulness
 */
export async function voteFAQ(faqId: string, helpful: boolean): Promise<void> {
  const field = helpful ? 'helpful_votes' : 'unhelpful_votes';
  
  const { data, error } = await supabase
    .from('help_faq')
    .select(field)
    .eq('id', faqId)
    .single();

  if (error || !data) {
    console.error('Error loading FAQ for vote:', error);
    return;
  }

  // Dynamic select(field) narrows `data` to a single-key object; index via Record
  const currentVotes = (data as Record<string, number | null | undefined>)[field] || 0;
  await supabase
    .from('help_faq')
    .update({ [field]: currentVotes + 1 })
    .eq('id', faqId);
}

// =====================================================
// Search
// =====================================================

/**
 * General help search (components, FAQ, actions)
 */
export async function searchHelp(query: string): Promise<HelpSearchResult[]> {
  const results: HelpSearchResult[] = [];

  // Search components
  const { data: components } = await supabase
    .from('help_components')
    .select('component_id, title, description, category')
    .or(`title.ilike.%${query}%,description.ilike.%${query}%`)
    .limit(5);

  if (components) {
    results.push(...components.map(c => ({
      id: c.component_id,
      type: 'component' as const,
      title: c.title,
      description: c.description,
      category: c.category as HelpCategory,
    })));
  }

  // Search FAQ
  const { data: faqs } = await supabase
    .from('help_faq')
    .select('id, question, answer, category')
    .eq('is_active', true)
    .or(`question.ilike.%${query}%,answer.ilike.%${query}%`)
    .limit(5);

  if (faqs) {
    results.push(...faqs.map(f => ({
      id: f.id,
      type: 'faq' as const,
      title: f.question,
      description: f.answer.substring(0, 150) + '...',
      category: f.category as HelpCategory,
    })));
  }

  // Track search
  await trackSearchQuery(query, results.length, results[0]?.id);

  return results;
}

// =====================================================
// Tours
// =====================================================

/**
 * Get available tours
 */
export async function getActiveTours(): Promise<HelpTour[]> {
  const { data, error } = await supabase
    .from('help_tours')
    .select('*')
    .eq('is_active', true)
    .order('title', { ascending: true });

  if (error) {
    console.error('Error loading tours:', error);
    return [];
  }

  return data.map(row => ({
    id: row.id,
    tourId: row.tour_id,
    title: row.title,
    description: row.description,
    category: row.category as HelpCategory,
    steps: row.steps,
    requiredRole: row.required_role,
    triggerContext: row.trigger_context,
    estimatedDurationMinutes: row.estimated_duration_minutes,
    difficulty: row.difficulty,
    startedCount: row.started_count || 0,
    completedCount: row.completed_count || 0,
    avgCompletionRate: row.avg_completion_rate,
    isActive: row.is_active,
    appVersion: row.app_version,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  }));
}

/**
 * Get user's progress on a tour
 */
export async function getTourProgress(userId: string, tourId: string): Promise<HelpTourProgress | null> {
  const { data, error } = await supabase
    .from('help_tour_progress')
    .select('*')
    .eq('user_id', userId)
    .eq('tour_id', tourId)
    .single();

  if (error || !data) {
    return null;
  }

  return {
    id: data.id,
    userId: data.user_id,
    tourId: data.tour_id,
    status: data.status,
    currentStep: data.current_step,
    totalSteps: data.total_steps,
    startedAt: data.started_at,
    completedAt: data.completed_at,
    lastInteractionAt: data.last_interaction_at,
    timeSpentSeconds: data.time_spent_seconds,
    createdAt: data.created_at,
    updatedAt: data.updated_at,
  };
}

// =====================================================
// Versions
// =====================================================

/**
 * Get latest app version info
 */
export async function getLatestVersion(): Promise<HelpVersion | null> {
  const { data, error } = await supabase
    .from('help_versions')
    .select('*')
    .order('release_date', { ascending: false })
    .limit(1)
    .single();

  if (error || !data) {
    return null;
  }

  return {
    id: data.id,
    version: data.version,
    releaseDate: data.release_date,
    title: data.title || '',
    summary: data.summary || '',
    changelog: data.changelog || {},
    componentsCount: data.components_count,
    actionsCount: data.actions_count,
    faqCount: data.faq_count,
    generatedAt: data.generated_at,
    deployedAt: data.deployed_at,
    createdAt: data.created_at,
  };
}

/**
 * Get all versions for changelog
 */
export async function getAllVersions(): Promise<HelpVersion[]> {
  const { data, error } = await supabase
    .from('help_versions')
    .select('*')
    .order('release_date', { ascending: false });

  if (error) {
    console.error('Error loading versions:', error);
    return [];
  }

  return data.map(row => ({
    id: row.id,
    version: row.version,
    releaseDate: row.release_date,
    title: row.title || '',
    summary: row.summary || '',
    changelog: row.changelog || {},
    componentsCount: row.components_count,
    actionsCount: row.actions_count,
    faqCount: row.faq_count,
    generatedAt: row.generated_at,
    deployedAt: row.deployed_at,
    createdAt: row.created_at,
  }));
}

// =====================================================
// Analytics
// =====================================================

/**
 * Track help interaction for analytics
 */
export async function trackHelpInteraction(interaction: Partial<HelpAnalytics>): Promise<void> {
  const { error } = await supabase
    .from('help_analytics')
    .insert({
      user_id: interaction.userId,
      session_id: interaction.sessionId,
      question: interaction.question || '',
      context: interaction.context,
      answer_source: interaction.answerSource,
      faq_id: interaction.faqId,
      component_id: interaction.componentId,
      llm_model: interaction.llmModel,
      tokens_used: interaction.tokensUsed || 0,
      response_time_ms: interaction.responseTimeMs,
      cost_usd: interaction.costUsd,
      was_helpful: interaction.wasHelpful,
      user_feedback: interaction.userFeedback,
      highlighted_elements: interaction.highlightedElements,
      completed_steps: interaction.completedSteps,
    });

  if (error) {
    console.error('Error tracking help interaction:', error);
  }
}

/**
 * Track search query
 */
async function trackSearchQuery(query: string, resultsCount: number, topResultId?: string): Promise<void> {
  const { error } = await supabase
    .from('help_search_queries')
    .insert({
      query,
      results_count: resultsCount,
      top_result_id: topResultId,
    });

  if (error) {
    console.error('Error tracking search query:', error);
  }
}

/**
 * Submit user feedback
 */
export async function submitFeedback(feedback: Omit<HelpFeedback, 'id' | 'createdAt' | 'updatedAt'>): Promise<void> {
  const { error } = await supabase
    .from('help_feedback')
    .insert({
      user_id: feedback.userId,
      feedback_type: feedback.feedbackType,
      subject: feedback.subject,
      details: feedback.details,
      page_url: feedback.pageUrl,
      component_id: feedback.componentId,
      screenshot_url: feedback.screenshotUrl,
      status: feedback.status,
    });

  if (error) {
    console.error('Error submitting feedback:', error);
  }
}

